/**
 * Show/hide rules of a form ("show this question when..."): ONE definition,
 * run by every screen that fills or previews a form.
 *
 *   CANONICAL  blhClinicApp/src/shared/lib/form-rules/fieldVisibility.js
 *   COPY       blhPatientApp/src/lib/form-rules/generated/fieldVisibility.js
 *
 * The patient app is a separate build, so it carries a byte-for-byte copy:
 * `npm run sync:form-rules` there copies this file and records its SHA-256,
 * and a test in each app pins that hash. Edit this file only here, then
 * re-sync; never edit the copy.
 *
 * WHERE THE RULES LIVE. The form builder (EditFormField) stores them on a
 * field as `conditionalRules.visibility = {condition: "AND"|"OR", rules:
 * [{field, operator, value}]}`, where `field` names an answer by its key
 * (below). A form is sections of rows of fields; a field is a wrapper ("Text",
 * "Check Boxes"...) holding a label and its control.
 *
 * WHAT A HIDDEN QUESTION MEANS, as FHIR R4 Questionnaire `enableWhen` does:
 *   - it is not shown and not required;
 *   - its answer does not count: a rule that reads a hidden question reads it
 *     as unanswered, even if an answer is kept in memory in case it shows
 *     again (so hiding cascades);
 *   - its answer is not saved or submitted (`withHiddenAnswersCleared`). The
 *     question keeps its place, so every position-based key stays what the
 *     rules say, and it is marked `hiddenByRule` so a document of the form
 *     (a PDF, a completed view) leaves it out.
 *
 * A rule that names an answer the form does not have (its field was moved or
 * removed after the rule was written) holds: a question is never hidden over
 * a rule nobody can satisfy, as an operator this code does not know never
 * hides one either.
 */

/** The mark a saved form carries on a question its rules hid. */
export const HIDDEN_BY_RULE = "hiddenByRule";

/** Whether a saved field was hidden by its rules when it was saved. */
export function isHiddenByRule(field) {
  return Boolean(field && field[HIDDEN_BY_RULE] === true);
}

const safeValue = (value) =>
  String(value || "")
    .replace(/[^a-zA-Z0-9]/g, "_")
    .substring(0, 20);

/** The value a checkbox stands for: its value, else its name. */
export function checkboxValueOf(box) {
  return box?.props?.value || box?.props?.name || "";
}

/**
 * The key of one checkbox's own answer (true/false), at the position of the
 * field that holds it: its id, else position and value.
 */
export function checkboxKeyOf(box, sectionIdx, rowIdx, colIdx) {
  return (
    box?.props?.id ||
    `cb_${sectionIdx}_${rowIdx}_${colIdx}_${safeValue(checkboxValueOf(box))}`
  );
}

/**
 * The id of a field at a position, which is the key of its answer: its own
 * id, else its name, else its database field, else its position. A checkbox
 * placed directly in a row is keyed as a checkbox.
 */
export function fieldIdOf(field, sectionIdx, rowIdx, colIdx) {
  const {element, props = {}, dbData} = field || {};
  if (element === "input" && props?.type === "checkbox") {
    return checkboxKeyOf(field, sectionIdx, rowIdx, colIdx);
  }
  return (
    props?.id ||
    props?.name ||
    dbData?.dbField ||
    `field_${sectionIdx}_${rowIdx}_${colIdx}`
  );
}

/** Every field of a form, in order, with its id and position. */
export function flattenFormFields(sections) {
  const fields = [];
  (Array.isArray(sections) ? sections : []).forEach((section, sectionIdx) => {
    (Array.isArray(section?.fields) ? section.fields : []).forEach(
      (row, rowIdx) => {
        (Array.isArray(row) ? row : []).forEach((field, colIdx) => {
          if (!field || typeof field !== "object") return;
          fields.push({
            ...field,
            fieldId: fieldIdOf(field, sectionIdx, rowIdx, colIdx),
            sectionIdx,
            rowIdx,
            colIdx,
          });
        });
      }
    );
  });
  return fields;
}

/** Whether a field carries at least one show/hide rule. */
export function hasVisibilityRules(field) {
  const rules = field?.conditionalRules?.visibility?.rules;
  return Array.isArray(rules) && rules.length > 0;
}

const isTicked = (v) => v === true || v === "true" || v === "yes" || v === "1";
const isUnticked = (v) =>
  v === false || v === "false" || v === "no" || v === "0" || !v;
const lower = (v) => String(v || "").toLowerCase();
const anyIncludes = (list, expected) =>
  list.some((v) => String(v).toLowerCase().includes(lower(expected)));

/** Whether one rule holds for the answer of the field it names. */
export function matchesRule(rule, answer) {
  const expected = rule?.value;
  switch (rule?.operator) {
    case "equals":
      // A checkbox group's answer is the list of the values ticked.
      if (Array.isArray(answer)) return answer.includes(expected);
      // Rules imported from XFA forms say "1"/"0" for ticked/unticked.
      if (expected === "1") return isTicked(answer);
      if (expected === "0") return isUnticked(answer);
      return String(answer || "") === String(expected || "");
    case "notEquals":
      if (Array.isArray(answer)) return !answer.includes(expected);
      if (expected === "1") return !isTicked(answer);
      if (expected === "0") return !isUnticked(answer);
      return String(answer || "") !== String(expected || "");
    case "contains":
      if (Array.isArray(answer)) return anyIncludes(answer, expected);
      return lower(answer).includes(lower(expected));
    case "notContains":
      if (Array.isArray(answer)) return !anyIncludes(answer, expected);
      return !lower(answer).includes(lower(expected));
    case "isEmpty":
      return !answer || (Array.isArray(answer) && answer.length === 0);
    case "isNotEmpty":
      return Boolean(answer) && !(Array.isArray(answer) && answer.length === 0);
    case "greaterThan":
      return parseFloat(answer) > parseFloat(expected);
    case "lessThan":
      return parseFloat(answer) < parseFloat(expected);
    case "greaterThanOrEqual":
      return parseFloat(answer) >= parseFloat(expected);
    case "lessThanOrEqual":
      return parseFloat(answer) <= parseFloat(expected);
    case "checked":
      if (Array.isArray(answer)) return answer.length > 0;
      return isTicked(answer);
    case "unchecked":
      if (Array.isArray(answer)) return answer.length === 0;
      return isUnticked(answer);
    case "startsWith":
      return lower(answer).startsWith(lower(expected));
    case "endsWith":
      return lower(answer).endsWith(lower(expected));
    default:
      // An operator this code does not know never hides a question.
      return true;
  }
}

/**
 * Whether a field's own rules hold for these answers (`{key: answer}`). A
 * field without rules is always shown. Rules join with AND unless the field
 * says OR. When `knownKeys` is given, a rule naming a key outside it holds.
 */
export function shouldShowField(field, answers, knownKeys) {
  if (!hasVisibilityRules(field)) return true;
  const {condition, rules} = field.conditionalRules.visibility;
  const values = answers || {};
  const results = rules.map((rule) =>
    knownKeys && !knownKeys.has(rule?.field)
      ? true
      : matchesRule(rule, values[rule?.field])
  );
  return condition === "OR" ? results.some(Boolean) : results.every(Boolean);
}

/** Every control (input, select, textarea) under a node, in order. */
export function controlsOf(node, found = []) {
  if (!node || typeof node !== "object") return found;
  if (["input", "select", "textarea"].includes(node.element)) found.push(node);
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => controlsOf(child, found));
  }
  return found;
}

const typeOf = (control) => control?.props?.type;
const hasValue = (v) => {
  if (Array.isArray(v)) return v.length > 0;
  if (v === undefined || v === null || v === false) return false;
  return String(v).trim() !== "";
};

/**
 * The answer a filled field holds, the way the rules read it: a control's
 * value; the list of values ticked in a checkbox group; true/false for a
 * single checkbox placed in a row; the value chosen among radios; the
 * signature; "saved" for a saved card. `undefined` for a field that asks
 * nothing (a heading, a paragraph).
 */
export function answerOfField(field) {
  if (!field || typeof field !== "object") return undefined;
  const slot = Array.isArray(field.children) ? field.children[1] : undefined;
  if (field.key === "Signature") {
    return typeof slot?.children === "string" ? slot.children : "";
  }
  if (field.key === "payment-method") {
    const card = slot?.children;
    return card && typeof card === "object" && card.saved === true
      ? "saved"
      : "";
  }
  const controls = controlsOf(field);
  if (controls.length === 0) return undefined;
  if (field.element === "input" && typeOf(field) === "checkbox") {
    return field.props?.checked === true;
  }
  const boxes = controls.filter((c) => typeOf(c) === "checkbox");
  if (boxes.length > 0) {
    return boxes
      .filter((box) => box.props?.checked === true)
      .map(checkboxValueOf);
  }
  const radios = controls.filter((c) => typeOf(c) === "radio");
  if (radios.length > 0) {
    const chosen = radios.find((radio) => radio.props?.checked === true);
    return chosen ? (chosen.props?.value ?? "") : "";
  }
  const typed = controls.find((c) => typeOf(c) !== "file");
  if (!typed) {
    const file = controls[0];
    return file.props?.fileFields?.[0]?.fileName || "";
  }
  const value = typed.props?.value ?? typed.props?.defaultValue;
  return value === undefined || value === null ? "" : value;
}

/**
 * Every key a field's answers are stored under, as the staff screens store
 * them, and what each stands for: the field's own answer (`of: "field"`);
 * each checkbox's own tick, with the value it stands for (`of: "box"`); each
 * radio group's choice, by the group's name (`of: "radio"`). The server reads
 * the same keys when it maps a form's answers to its questions.
 */
export function answerSlotsOfField(field, sectionIdx, rowIdx, colIdx) {
  const slots = [];
  if (answerOfField(field) !== undefined) {
    slots.push({
      key: fieldIdOf(field, sectionIdx, rowIdx, colIdx),
      of: "field",
    });
  }
  const isBox = field.element === "input" && typeOf(field) === "checkbox";
  const controls = isBox ? [] : controlsOf(field);
  controls
    .filter((c) => typeOf(c) === "checkbox")
    .forEach((box) => {
      slots.push({
        key: checkboxKeyOf(box, sectionIdx, rowIdx, colIdx),
        of: "box",
        value: checkboxValueOf(box),
      });
    });
  const radios = controls.filter((c) => typeOf(c) === "radio");
  new Set(radios.map((r) => r.props?.name).filter(Boolean)).forEach((name) => {
    slots.push({key: name, of: "radio"});
  });
  return slots;
}

/**
 * Every answer a field gives, under every key the staff screens store it by:
 * the field's own key; each checkbox's own key (true/false); each radio
 * group's name (the value chosen).
 */
function answersOfField(field, sectionIdx, rowIdx, colIdx) {
  const isBox = field.element === "input" && typeOf(field) === "checkbox";
  const controls = isBox ? [] : controlsOf(field);
  // The slots name the boxes in their order, one each, so the nth box slot is
  // the nth box, even when two boxes share a key.
  const boxes = controls.filter((c) => typeOf(c) === "checkbox");
  const radios = controls.filter((c) => typeOf(c) === "radio");
  let boxAt = 0;
  return answerSlotsOfField(field, sectionIdx, rowIdx, colIdx).map((slot) => {
    if (slot.of === "box") {
      const box = boxes[boxAt++];
      return [slot.key, box?.props?.checked === true];
    }
    if (slot.of === "radio") {
      const chosen = radios.find(
        (r) => r.props?.name === slot.key && r.props?.checked === true
      );
      return [slot.key, chosen ? (chosen.props?.value ?? "") : ""];
    }
    return [slot.key, answerOfField(field)];
  });
}

/**
 * The answers of a form whose answers live in its own tree (a patient form
 * submission), keyed as the staff screens key theirs. When two fields share a
 * key, the first one answered speaks for it.
 */
export function answersFromSections(sections) {
  const answers = {};
  flattenFormFields(sections).forEach((field) => {
    answersOfField(field, field.sectionIdx, field.rowIdx, field.colIdx).forEach(
      ([key, answer]) => {
        const known = answers[key];
        if (known === undefined || (!hasValue(known) && hasValue(answer))) {
          answers[key] = answer;
        }
      }
    );
  });
  return answers;
}

/** Every key an answer of this form can be stored under. */
export function answerKeysOf(sections) {
  const keys = new Set();
  flattenFormFields(sections).forEach((field) => {
    keys.add(field.fieldId);
    answersOfField(field, field.sectionIdx, field.rowIdx, field.colIdx).forEach(
      ([key]) => keys.add(key)
    );
  });
  return keys;
}

/**
 * Which fields are shown, `{fieldId: boolean}`, for these answers (keyed as
 * `answersFromSections` keys them, or as the staff screens store theirs).
 *
 * Settled in passes: a question hidden in one pass loses its answer for the
 * next, so a question that depends on it is judged as if it were unanswered
 * (FHIR). Rules that feed each other in a loop could flip for ever; after as
 * many passes as there are ruled fields, a field that is still flipping is
 * SHOWN, because hiding a question nobody can predict is worse than asking it.
 * When fields share an id, the id is shown if any of them is.
 */
export function visibilityOfSections(sections, answers) {
  const fields = flattenFormFields(sections);
  const ruled = fields.filter(hasVisibilityRules);
  const known = ruled.length > 0 ? answerKeysOf(sections) : new Set();
  const given = answers || {};

  const hiddenIn = (hidden) => {
    const effective = {...given};
    hidden.forEach((id) => {
      delete effective[id];
    });
    const shown = new Set();
    const next = new Set();
    ruled.forEach((field) => {
      if (shouldShowField(field, effective, known)) shown.add(field.fieldId);
      else next.add(field.fieldId);
    });
    // An id another field shows stays shown.
    fields.forEach((field) => {
      if (!hasVisibilityRules(field)) shown.add(field.fieldId);
    });
    shown.forEach((id) => next.delete(id));
    return next;
  };
  const same = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));
  const both = (a, b) => new Set([...a].filter((id) => b.has(id)));

  let hidden = new Set();
  let settled = ruled.length === 0;
  for (let pass = 0; !settled && pass <= ruled.length; pass += 1) {
    const next = hiddenIn(hidden);
    if (same(next, hidden)) {
      settled = true;
    } else if (pass === ruled.length) {
      hidden = both(next, hidden);
    } else {
      hidden = next;
    }
  }

  const visibility = {};
  fields.forEach((field) => {
    visibility[field.fieldId] = !hidden.has(field.fieldId);
  });
  return visibility;
}

/** Whether the field at this position is shown, from `visibilityOfSections`. */
export function isShownAt(visibility, sections, sectionIdx, rowIdx, colIdx) {
  const field = sections?.[sectionIdx]?.fields?.[rowIdx]?.[colIdx];
  if (!field || typeof field !== "object") return true;
  return visibility?.[fieldIdOf(field, sectionIdx, rowIdx, colIdx)] !== false;
}

function withoutAnswer(node) {
  if (Array.isArray(node)) return node.map(withoutAnswer);
  if (!node || typeof node !== "object") return node;
  const copy = {...node};
  if (node.props && typeof node.props === "object") {
    const props = {...node.props};
    const type = props.type;
    if (node.element === "input" && (type === "checkbox" || type === "radio")) {
      props.checked = false;
    } else if (node.element === "input" && type === "file") {
      delete props.fileFields;
    } else if (["input", "select", "textarea"].includes(node.element)) {
      delete props.value;
      delete props.defaultValue;
    }
    copy.props = props;
  }
  if (Array.isArray(node.children)) {
    copy.children = node.children.map(withoutAnswer);
  }
  if (
    (node.key === "Signature" || node.key === "payment-method") &&
    Array.isArray(copy.children) &&
    copy.children[1] &&
    typeof copy.children[1] === "object"
  ) {
    copy.children[1] = {...copy.children[1], children: ""};
  }
  return copy;
}

function unmarked(field) {
  if (!isHiddenByRule(field)) return field;
  const copy = {...field};
  delete copy[HIDDEN_BY_RULE];
  return copy;
}

/**
 * The sections to SAVE or SUBMIT: every question keeps its place, a hidden
 * one without its answer and marked `hiddenByRule`, a shown one unmarked.
 * Returns copies; the sections passed in are not changed. A form without
 * rules comes back with the same rows, unchanged.
 */
export function withHiddenAnswersCleared(sections, visibility) {
  return (Array.isArray(sections) ? sections : []).map(
    (section, sectionIdx) => {
      if (!Array.isArray(section?.fields)) return section;
      const rows = section.fields.map((row, rowIdx) => {
        if (!Array.isArray(row)) return row;
        let touched = false;
        const cells = row.map((field, colIdx) => {
          if (!field || typeof field !== "object") return field;
          const shown = isShownAt(
            visibility,
            sections,
            sectionIdx,
            rowIdx,
            colIdx
          );
          const next = shown
            ? unmarked(field)
            : {...withoutAnswer(field), [HIDDEN_BY_RULE]: true};
          if (next !== field) touched = true;
          return next;
        });
        return touched ? cells : row;
      });
      return {...section, fields: rows};
    }
  );
}
